import { Fragment, type ReactNode } from 'react';
import { cn, formatCurrency } from '@/lib/utils';
import { formatBillingDate } from './billingDate';
import { AgingBadge } from './AgingBadge';
import { DueCell, InvoiceAmount, KindBadge, LifecycleBadge, SentDateCell } from './InvoiceCells';
import { InvoiceRowActions } from './InvoiceRowActions';
import { SendOutcomeBadge } from './SendOutcomeBadge';
import type { InvoiceRowActionHandlers } from './InvoiceTable';
import type { BillingInvoiceRow, InvoiceColumns } from './invoiceRow';

/**
 * The mobile pass, matching the JobListTable / JobListCards split.
 *
 * Recording a check should not require a desktop (spec §6), so the card
 * carries the same actions menu as the table row rather than a reduced one.
 */

/** Generic for the same reason as InvoiceTable — see the note there. */
export interface InvoiceCardsProps<T extends BillingInvoiceRow = BillingInvoiceRow> {
  rows: T[];
  columns: InvoiceColumns;
  actionsFor?: (row: T) => InvoiceRowActionHandlers;
  onRowClick?: (row: T) => void;
  groupHeaderFor?: (row: T, index: number) => ReactNode;
  notesCellFor?: (row: T) => ReactNode;
  emptyState?: ReactNode;
  className?: string;
}

export function InvoiceCards<T extends BillingInvoiceRow>({
  rows,
  columns,
  actionsFor,
  onRowClick,
  groupHeaderFor,
  notesCellFor,
  emptyState,
  className,
}: InvoiceCardsProps<T>) {
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  return (
    <div className={cn('space-y-2', className)}>
      {rows.map((row, index) => {
        const header = groupHeaderFor?.(row, index);
        const actions = actionsFor?.(row);
        const voided = row.lifecycle === 'voided';

        return (
          <Fragment key={row.invoiceId}>
            {header && <div className="px-1 pt-2">{header}</div>}
            <div
              className={cn(
                'rounded-lg border bg-white p-3',
                voided && 'opacity-50',
                row.kind === 'final' && !voided && 'bg-slate-50',
                onRowClick && 'cursor-pointer active:bg-gray-50',
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn('font-mono text-sm', voided && 'line-through')}>
                      {row.invoiceNumber}
                    </span>
                    <KindBadge kind={row.kind} isCash={row.isCash} />
                  </div>
                  {columns.client && row.clientName && (
                    <p className="truncate text-sm text-gray-700">{row.clientName}</p>
                  )}
                  {columns.job && row.jobCode && (
                    <p className="truncate text-xs text-gray-500">
                      <span className="font-mono">{row.jobCode}</span>
                      {row.jobDescription ? ` · ${row.jobDescription}` : ''}
                    </p>
                  )}
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  {actions && <InvoiceRowActions invoice={row} {...actions} />}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <InvoiceAmount
                  row={row}
                  onAmend={actions?.onAmendAmount as (() => void) | undefined}
                  className="font-medium"
                />
                {columns.collected && !row.amountUnknown && row.outstanding > 0 && (
                  <span className="tabular-nums text-red-600">
                    {formatCurrency(row.outstanding)} due
                  </span>
                )}
                {row.dueDate && (
                  <span className="text-gray-500">
                    Due <DueCell row={row} />
                  </span>
                )}
              </div>

              {/* The strip already wraps, so the bounce badge is one more chip. */}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <LifecycleBadge lifecycle={row.lifecycle} />
                {columns.sendOutcome && (
                  <SendOutcomeBadge state={row.lastSendState} detail={row.lastSendDetail} />
                )}
                {columns.aging && (
                  <AgingBadge
                    bucket={row.agingBucket}
                    daysOutstanding={row.daysOutstanding}
                    isOverdue={row.isOverdue}
                  />
                )}
                {/*
                  Recording that an invoice was texted to a client is a phone
                  job by nature, so the card carries the same editable cell the
                  table row does rather than a read-only date.
                */}
                {columns.sentDate && (
                  <span
                    className="text-xs text-gray-500"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Sent <SentDateCell row={row} onSetSentDate={actions?.onSetSentDate} className="text-xs" />
                  </span>
                )}
                {columns.paidDate && row.paidAt && (
                  <span className="text-xs text-gray-500">Paid {formatBillingDate(row.paidAt)}</span>
                )}
              </div>

              {columns.notes && notesCellFor && (
                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                  {notesCellFor(row)}
                </div>
              )}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
